import { useQuery } from '@tanstack/react-query'
import { monadClient } from '../api/client'
import { attributesQuery } from '../api/typeMetadata'

/** A hop in a picked path. `sub_field` is set only on a composite's
 *  leaf hop — the one shared representation every consumer reads. */
export type HopPath = { type_id: string; attribute_id: string; sub_field?: string; format?: string | null }[]

export interface SubFieldMeta {
  name: string
  /** Server-owned human label; older payloads fall back to the storage key. */
  label?: string
  type: string
  required: boolean
  /** SEN-1780: the CONSTRAINT kind the server's codec enforces for this
   *  sub-field (`country-iso2`, `latitude`, …). Served alongside `type`,
   *  which stays the generic storage kind. Absent on older payloads and on
   *  derived sub-fields — consumers fall back to `type`. */
  kind?: string | null
  /** ADR-0030 §4: derived ⇒ read-only (composite sub-fields). Absent in older
   *  test fixtures; consumers treat undefined as writable. */
  read_only?: boolean
  /** Served from the leaf type registry; drives filter-picker eligibility. */
  filter_operators?: string[] | null
}

/** ADR-0030 C2 — a repeated (list-of-struct) sub-structure. `entry` declares the
 *  per-entry fixed struct as dot-path leaf sub-fields (e.g. open.day, open.time). */
export interface RepeatedSubStructure {
  repeated: true
  entry: SubFieldMeta[]
}

/** The three wire shapes of `sub_structure` (monad/api/serialization.py
 *  enrich_sub_structure): C1 flat list, C2 repeated object, or null. */
export type SubStructure = SubFieldMeta[] | RepeatedSubStructure | null

/** Normalize any `sub_structure` wire shape to the flat SubFieldMeta[] every
 *  consumer reads. C1 passes through; C2 (repeated) exposes its entry fields
 *  (the dot-path leaves are addressable exactly like C1 sub-fields — the
 *  ∃-quantifier "every entry, not one" semantics are deferred query work, ADR-0030
 *  §C2); null/undefined → []. One boundary — no Array.isArray checks at call sites. */
export function normalizeSubStructure(subs: SubStructure | undefined): SubFieldMeta[] {
  if (Array.isArray(subs)) return subs
  if (subs && subs.repeated === true) return subs.entry
  return []
}

/** Scalar dimension leaves only. C2 entry fields require quantified list
 * semantics and cannot be sent to the current dim-cell resolver as a simple
 * dotted column. */
export function scalarSubFields(subs: SubStructure | undefined): SubFieldMeta[] {
  return Array.isArray(subs) ? subs : []
}

/** Picker's view of an attribute. A subset of the monad Attribute schema,
 *  extended with reference-drilling fields that come through .passthrough(). */
export interface PickerAttr {
  attribute_id: string
  name: string
  type: string
  /** Present on entity-reference and actor-reference attributes when the backend
   *  has enriched the row with a target type id. Null means dangling/unknown. */
  target_type_id?: string | null
  is_multiselect?: boolean
  sortable?: boolean
  /** Present on composite attributes (SEN-671). Drillable into sub-fields. */
  sub_structure?: SubStructure
  /** Phase 1: op-string list from the wire. Drives isFilterableAttr at the
   *  filter picker leaf. Present via .passthrough() on the wire schema. */
  filter_operators?: string[] | null
  /** Constraint kind for a synthetic composite leaf; storage `type` remains stable. */
  sub_field_kind?: string | null
  /** Storable presentation variants served by the kind metadata. */
  formats?: string[] | null
  /** Composite projection declarations, transported verbatim by the API. */
  display_spec?: { projections?: Record<string, { default?: boolean; [key: string]: unknown }> } | null
}

const REFERENCE_KINDS = new Set(['entity-reference', 'actor-reference'])

/** Returns the target type id if `attr` is a drillable reference, else null.
 *  Dangling refs (target_type_id absent or null) are treated as leaves. */
export function resolveTarget(attr: PickerAttr): string | null {
  if (!REFERENCE_KINDS.has(attr.type)) return null
  return attr.target_type_id ?? null
}

/** True when the attribute cannot be drilled into (non-reference or dangling ref). */
export function isLeaf(attr: PickerAttr): boolean {
  return resolveTarget(attr) === null
}

/** Append one hop to a path. Pure — returns a new array; caller's path is unchanged. */
export function pushHop(path: HopPath, typeId: string, attr: PickerAttr): HopPath {
  return [...path, { type_id: typeId, attribute_id: attr.attribute_id }]
}

/** Remove the last hop from a path. Pure — returns a new array. */
export function popHop(path: HopPath): HopPath {
  return path.slice(0, -1)
}

/** Synthesize a composite's sub-fields as pseudo-leaf PickerAttrs. Each carries
 *  its own declared `type` so the per-surface leafFilter judges it; the
 *  `attribute_id` stays the PARENT's (the sub_field rides the same leaf hop). */
export function subFieldsOf(attr: PickerAttr): (PickerAttr & { sub_field: string })[] {
  return normalizeSubStructure(attr.sub_structure).map((s) => ({
    attribute_id: attr.attribute_id,
    name: s.label ?? s.name,
    type: s.type,
    sub_field_kind: s.kind ?? s.type,
    sub_field: s.name,
    filter_operators: s.filter_operators,
  }))
}

/** Add-column children for a composite: every declared non-default projection
 * and every sub-field, deduplicated by its wire format. A projection wins a
 * name collision while the matching sub_field still rides the hop, preserving
 * filter/sort semantics (location.country is the canonical example). */
export function columnVariantsOf(
  attr: PickerAttr,
): (PickerAttr & { format: string; sub_field?: string })[] {
  const fields = new Map(normalizeSubStructure(attr.sub_structure).map((field) => [field.name, field]))
  return (attr.formats ?? []).map((format) => {
    const field = fields.get(format)
    return {
      attribute_id: attr.attribute_id,
      name: format.replace(/_/g, ' ').replace(/\b\w/g, (letter: string) => letter.toUpperCase()),
      type: field?.type ?? attr.type,
      format,
      ...(field ? { sub_field: field.name } : {}),
    }
  })
}

/** A composite (non-empty sub_structure, C1 or C2) is drillable into sub-fields. */
export function hasSubFields(attr: PickerAttr): boolean {
  return normalizeSubStructure(attr.sub_structure).length > 0
}

/** Fetch the attributes for a type, shaped as PickerAttr[].
 *
 *  The monad Attribute schema uses .passthrough(), so runtime objects carry
 *  extra fields (e.g. target_type_id) that are absent from the static Zod
 *  type. We cast through `unknown` here — the cast is safe because passthrough
 *  is the schema's explicit contract for allowing additional fields. */
export function useAttributeLevel(slug: string, typeId: string) {
  // SEN-180: key + caching from the ONE seam (api/typeMetadata) so this shares
  // the entry every other attributes reader uses; only the PickerAttr cast and
  // the `enabled` guard are local.
  return useQuery({
    ...attributesQuery(slug, typeId),
    queryFn: () =>
      monadClient
        .getAttributes(slug, typeId)
        .then((attrs) => attrs as unknown as PickerAttr[]),
    enabled: !!typeId,
  })
}
